import React, { FormEvent, LegacyRef, forwardRef, useEffect } from 'react';
import TabPanel from '../../../components/TabPanel/TabPanel';
import { Tabs, Tab, Button, TextField } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { PaymentMethodsInfo, PaymentOptions } from '../Deposit.types';
import { IonSpinner } from '@ionic/react';
import {
  AvailablePaymentGateways,
  AvailablePaymentMethods,
  PaymentMethodLabels,
} from '../../../util/stringUtil';
import QRCode from 'react-qr-code';

interface AbcMoneyTabPanelProps {
  index: number;
  onlinePaymentOption: string;
  setOnlinePaymentOption: (paymentOption: string) => void;
  tabValue: number;
  submitOnlineAmount: (e: FormEvent, selectedPaymentGateway?: string) => void;
  paymentDetails: PaymentOptions[];
  depositAmount: string;
  setDepositAmount: (amount: string) => void;
  mobileNumber: string;
  setMobileNumber: (amount: string) => void;
  loading: boolean;
  providerRefId: string;
  confirmPayment: (e: FormEvent, selectedPaymentGateway?: string) => void;
  copyText: (text: string) => void;
  referenceId: string;
  setReferenceId: (amount: string) => void;
  paymentOptionFilter: string;
  setPaymentOptionFilter: (amount: string) => void;
  depositImage: string | ArrayBuffer;
  handleCapture: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClick: () => void;
  setProviderRefId?: (refId: string) => void;
  setDepositImage?: (imageData: string | ArrayBuffer) => void;
  depositPaymentMethodsInfo: PaymentMethodsInfo;
}

const AbcMoney = forwardRef<HTMLInputElement, AbcMoneyTabPanelProps>(
  (
    {
      index,
      onlinePaymentOption,
      setOnlinePaymentOption,
      tabValue,
      submitOnlineAmount,
      paymentDetails,
      depositAmount,
      setDepositAmount,
      mobileNumber,
      setMobileNumber,
      loading,
      providerRefId,
      confirmPayment,
      copyText,
      referenceId,
      setReferenceId,
      paymentOptionFilter,
      setPaymentOptionFilter,
      depositImage,
      handleCapture,
      handleClick,
      depositPaymentMethodsInfo,
    },
    hiddenFileInput
  ) => {
    const paymentMethodsList =
      depositPaymentMethodsInfo[AvailablePaymentGateways.ABCMONEY];

    useEffect(() => {
      if (paymentMethodsList?.length > 0)
        setOnlinePaymentOption(paymentMethodsList[0]);
    }, [paymentMethodsList]);

    return (
      <TabPanel value={tabValue} index={index}>
        <>
          <div className="payment-option-title mt-10">
            Choose Payment Option
          </div>
          <Tabs
            value={onlinePaymentOption}
            onChange={(_, newValue) => {
              setOnlinePaymentOption(newValue);
            }}
          >
            {paymentMethodsList &&
              paymentMethodsList.length > 0 &&
              paymentMethodsList.map((paymentMethodName) => (
                <Tab
                  value={
                    paymentMethodName === AvailablePaymentMethods.BANK
                      ? AvailablePaymentMethods.BANK_TRANSFER
                      : paymentMethodName
                  }
                  label={PaymentMethodLabels[paymentMethodName]}
                />
              ))}
          </Tabs>
          {onlinePaymentOption && (
            <form
              className="account-inputs"
              onSubmit={(e) => submitOnlineAmount(e)}
            >
              <div className="account-input">
                <div className="account-label">Enter Amount</div>
                <NumberFormat
                  required
                  className={
                    paymentDetails?.length && paymentDetails[0]?.payment_method
                      ? 'disabled acc-input'
                      : 'acc-input'
                  }
                  placeholder="Enter Amount to be Deposited"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  decimalSeparator={false}
                  disabled={
                    paymentDetails?.length && paymentDetails[0]?.payment_method
                      ? true
                      : false
                  }
                />
              </div>
              <div className="account-input">
                <div className="account-label">Enter Mobile Number</div>
                <NumberFormat
                  required
                  className={
                    paymentDetails?.length && paymentDetails[0]?.payment_method
                      ? 'disabled acc-input'
                      : 'acc-input'
                  }
                  placeholder="Enter Mobile Number"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  decimalSeparator={false}
                  disabled={
                    paymentDetails?.length && paymentDetails[0]?.payment_method
                      ? true
                      : false
                  }
                />
              </div>
              {!providerRefId && (
                <Button
                  className="submit-payment-btn"
                  type="submit"
                  disabled={loading ? true : false}
                  endIcon={loading ? <IonSpinner name="lines-small" /> : ''}
                >
                  Next
                </Button>
              )}
            </form>
          )}

          {paymentDetails?.length > 0 && paymentDetails[0]?.payment_method && (
            <form
              className="account-inputs"
              onSubmit={(e) => confirmPayment(e)}
            >
              <div className="payment-option-title mt-10">Account Details</div>

              {paymentDetails?.length > 0 &&
                paymentDetails[0]?.payment_method &&
                onlinePaymentOption === 'BANK_TRANSFER' && (
                  <div className="account-inputs">
                    <div className="account-input">
                      <div className="payment-detail">
                        Account Holder Name:
                        <span
                          title="Click to Copy"
                          className="payment-detail-input"
                          onClick={() =>
                            copyText(
                              paymentDetails?.filter(
                                (i) => i?.payment_method === onlinePaymentOption
                              )[0]?.payment_method_details?.bank_account_holder
                            )
                          }
                        >
                          {
                            paymentDetails?.filter(
                              (i) => i?.payment_method === onlinePaymentOption
                            )[0]?.payment_method_details?.bank_account_holder
                          }
                        </span>
                      </div>
                      <div className="payment-detail">
                        Account Holder Number:
                        <span
                          title="Click to Copy"
                          className="payment-detail-input"
                          onClick={() =>
                            copyText(
                              paymentDetails?.filter(
                                (i) => i?.payment_method === onlinePaymentOption
                              )[0]?.payment_method_details?.bank_account_number
                            )
                          }
                        >
                          {
                            paymentDetails?.filter(
                              (i) => i?.payment_method === onlinePaymentOption
                            )[0]?.payment_method_details?.bank_account_number
                          }
                        </span>
                      </div>
                      <div className="payment-detail">
                        Account IFSC Code:
                        <span
                          title="Click to Copy"
                          className="payment-detail-input"
                          onClick={() =>
                            copyText(
                              paymentDetails?.filter(
                                (i) => i?.payment_method === onlinePaymentOption
                              )[0]?.payment_method_details?.ifs_code
                            )
                          }
                        >
                          {
                            paymentDetails?.filter(
                              (i) => i?.payment_method === onlinePaymentOption
                            )[0]?.payment_method_details?.ifs_code
                          }
                        </span>
                      </div>
                      <div className="payment-detail">
                        Bank Name:
                        <span
                          title="Click to Copy"
                          className="payment-detail-input"
                          onClick={() =>
                            copyText(
                              paymentDetails?.filter(
                                (i) => i?.payment_method === onlinePaymentOption
                              )[0]?.payment_method_details?.bank_name
                            )
                          }
                        >
                          {paymentDetails?.filter(
                            (i) => i?.payment_method === onlinePaymentOption
                          )[0]?.payment_method_details?.bank_name ?? '--'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              {onlinePaymentOption === 'UPI' && (
                <div className="account-inputs">
                  <div className="account-input">
                    <div
                      style={{
                        height: 'auto',
                        margin: '0px',
                        maxWidth: 128,
                        width: '100%',
                      }}
                    >
                      <QRCode
                        size={256}
                        style={{
                          height: 'auto',
                          maxWidth: '100%',
                          width: '100%',
                        }}
                        value={`upi://pay?pa=${
                          paymentDetails?.filter(
                            (i) => i?.payment_method === onlinePaymentOption
                          )[0]?.payment_method_details?.upi_id
                        }&am=${depositAmount}&tn=pay to eswar&cu=INR&pn=${
                          paymentDetails?.filter(
                            (i) => i?.payment_method === onlinePaymentOption
                          )[0].payment_method_details.bank_account_holder
                        }`}
                        viewBox={`0 0 256 256`}
                      />
                    </div>
                    <div className="payment-detail">
                      UPI ID:
                      <span
                        title="Click to Copy"
                        className="payment-detail-input"
                        onClick={() =>
                          copyText(
                            paymentDetails?.filter(
                              (i) => i?.payment_method === onlinePaymentOption
                            )[0]?.payment_method_details?.upi_id
                          )
                        }
                      >
                        {
                          paymentDetails?.filter(
                            (i) => i?.payment_method === onlinePaymentOption
                          )[0]?.payment_method_details?.upi_id
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="account-input">
                <div className="account-label">
                  Enter Reference ID/UTR (Re-verify for correctness)
                </div>
                <TextField
                  required
                  className="acc-input"
                  placeholder="Enter Reference ID/UTR"
                  value={referenceId}
                  onChange={(e) => setReferenceId(e.target.value)}
                />
              </div>

              <div className="account-input">
                <div className="account-label">Select Payment Option</div>
                <select
                  required
                  value={paymentOptionFilter}
                  placeholder="Select One"
                  className="acc-input"
                  onChange={(e) => setPaymentOptionFilter(e.target.value)}
                >
                  <option value="IMPS">IMPS</option>
                  <option value="NEFT">NEFT</option>
                  <option value="RTGS">RTGS</option>
                </select>
              </div>

              <div className="account-input">
                {depositImage ? (
                  <>
                    <div>
                      <div className="uploaded-image-title">Uploaded Image</div>
                      <img src={`${depositImage}`} className="deposit-image" />
                    </div>
                  </>
                ) : null}

                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="raised-button-file-abcmoney"
                  multiple
                  hidden
                  type="file"
                  ref={hiddenFileInput}
                  onChange={(e) => handleCapture(e)}
                />
                <Button
                  component="div"
                  className="upload-btn"
                  onClick={handleClick}
                >
                  Upload Image
                </Button>
              </div>
              <Button
                className="submit-payment-btn"
                type="submit"
                disabled={loading ? true : false}
                endIcon={loading ? <IonSpinner name="lines-small" /> : ''}
              >
                Confirm Payment
              </Button>
            </form>
          )}
        </>
      </TabPanel>
    );
  }
);

export default AbcMoney;
